// src/hooks/useGroupWorkoutQueries.ts
import { useQuery, gql, ApolloError } from '@apollo/client';
import { GroupWorkoutSession } from 'interfaces/group-workout';
import { useGroupWorkoutSessionActions, useGroupWorkoutSessionById, useCurrentGroupWorkoutSession } from 'store/group-workout-session/selectors';
import useGroupWorkoutSessionStore from 'store/group-workout-session';
import { useEffect, useRef } from 'react';

const getGroupWorkoutConnectionPollInterval = () => {
  const configuredInterval = Number(process.env.REACT_APP_GROUP_WORKOUT_CONNECTION_POLL_INTERVAL_MS);

  return Number.isFinite(configuredInterval) && configuredInterval > 0 ? configuredInterval : 10000;
};

const GROUP_WORKOUT_CONNECTION_POLL_INTERVAL_MS = getGroupWorkoutConnectionPollInterval();
const compareSortOrder = (a: { sortOrder: number }, b: { sortOrder: number }) => a.sortOrder - b.sortOrder;

export const normalizeGroupWorkoutSessionOrder = (session: GroupWorkoutSession): GroupWorkoutSession => ({
  ...session,
  groupWorkoutSessionTracks: [...(session.groupWorkoutSessionTracks ?? [])]
    .sort(compareSortOrder)
    .map((track) => ({
      ...track,
      groupWorkoutSessionRounds: [...(track.groupWorkoutSessionRounds ?? [])].sort(compareSortOrder),
    })),
});

const GET_GROUP_WORKOUT_SESSION = gql`
  query GroupWorkoutSession($id: String!) {
    groupWorkoutSession(input: { id: $id }) {
      id
      groupWorkout {
        id
      }
      groupBags {
        id
        isEnabled
        bag {
          id
          name
          reference
          rank
          latestWifiStrength
          averageReceivedPackets
          latestRebootCount
        }
      }
      groupWorkoutSessionTracks {
        id
        trackId
        sortOrder
        groupWorkoutSessionRounds {
          id
          roundId
          sortOrder
          groupWorkoutHits {
            id
            force
            punchedAt
            bag {
              id
            }
          }
        }
      }
    }
  }
`;

/**
 * Custom hook to fetch a single group workout by ID.
 * Upon successful fetch, it updates the GroupWorkoutStore.
 * Manages loading and error states via Zustand.
 */
export const useGroupWorkoutSessionByIdQuery = (
  id: string,
  options?: { skip?: boolean }
) => {
  const { setGroupWorkoutSessionById, syncGroupWorkoutSessionConnectionState, setLoading, setError } = useGroupWorkoutSessionActions();
  const existingWorkoutSession = useGroupWorkoutSessionById(id); // Ensure this Hook is correctly defined
  const currentWorkoutSession = useCurrentGroupWorkoutSession();
  const shouldSkipQuery = !id || (!!options?.skip && !existingWorkoutSession && !currentWorkoutSession);
  const processedSessionRef = useRef<GroupWorkoutSession | null>(null);

  useEffect(() => {
    if (existingWorkoutSession && (!currentWorkoutSession || currentWorkoutSession.id !== existingWorkoutSession.id)) {
      setGroupWorkoutSessionById(existingWorkoutSession);
    }
  }, [currentWorkoutSession, existingWorkoutSession, setGroupWorkoutSessionById]);

  // Initiate side effects
  const { loading, error, data } = useQuery<{ groupWorkoutSession: GroupWorkoutSession[] }>(GET_GROUP_WORKOUT_SESSION, {
    variables: { id },
    fetchPolicy: 'no-cache',
    pollInterval: GROUP_WORKOUT_CONNECTION_POLL_INTERVAL_MS,
    onError: (error: ApolloError) => {
      console.error(`Error fetching group workout session with ID ${id}:`, error.message);
      setError(error.message);
      setLoading(false);
    },
    skip: shouldSkipQuery,
  });

  useEffect(() => {
    if (!data?.groupWorkoutSession || data.groupWorkoutSession.length === 0) return;

    const sourceWorkoutSession = data.groupWorkoutSession[0];
    if (processedSessionRef.current === sourceWorkoutSession) return;

    processedSessionRef.current = sourceWorkoutSession;
    const nextWorkoutSession = normalizeGroupWorkoutSessionOrder(sourceWorkoutSession);
    const currentSession = useGroupWorkoutSessionStore.getState().currentGroupWorkoutSession;

    if (!currentSession || currentSession.id !== nextWorkoutSession.id) {
      setGroupWorkoutSessionById(nextWorkoutSession);
    } else {
      syncGroupWorkoutSessionConnectionState(nextWorkoutSession);
    }

    setLoading(false);
  }, [data, setGroupWorkoutSessionById, setLoading, syncGroupWorkoutSessionConnectionState]);

  useEffect(() => {
    setLoading(loading);
    if (error) {
      setError(error.message);
    }
  }, [loading, error, setLoading, setError]);

  return null;
};
